import { WORKSPACE_PADDING, WORKSPACE_PADDING_MOBILE } from '@components/v2/constants';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import { Box, Container, useTheme } from '@mui/material';
import { Pencil } from 'lucide-react';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import LangEnum from '../../lang/LangEnum';
import { useTranslation } from '../../modules/translation/TranslationContext';
import { ShowroomTestIds } from '../../utils/testIds/Showroom.testIds';
import useWidth from '../Hooks/useWidth';

export type SectionBgColor = 'white' | 'gray';

export type SectionProps = {
  children: React.ReactNode;
  bgColor?: SectionBgColor;
  readOnly?: boolean;
  editLabel?: LangEnum;
  onEdit?: () => void;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginInline: theme.spacing(-WORKSPACE_PADDING),
    paddingInline: theme.spacing(WORKSPACE_PADDING),
    [theme.breakpoints.down('sm')]: {
      marginInline: theme.spacing(-WORKSPACE_PADDING_MOBILE),
      paddingInline: theme.spacing(WORKSPACE_PADDING_MOBILE),
    },
  },
  button: {
    position: 'absolute',
    zIndex: 1,
  },
}));

const Section = ({
  children,
  bgColor = 'white',
  readOnly = true,
  editLabel,
  onEdit,
}: SectionProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { mobileWidth } = useWidth();
  const { translate } = useTranslation();
  if (!children) {
    return null;
  }

  return (
    <Box
      test-id={ShowroomTestIds.FEATURED_SHOWROOM_CONTAINER_DETAILS}
      className={classes.root}
      style={{
        backgroundColor: bgColor === 'gray' ? theme.palette.background.default : 'custom.workspace',
      }}
      sx={{
        paddingY: mobileWidth ? 5 : 4,
      }}
    >
      {!readOnly && onEdit && (
        <Button
          size="medium"
          variant="contained"
          color="primary"
          className={classes.button}
          startIcon={<Icon icon={Pencil} />}
          onClick={onEdit}
        >
          {translate(editLabel ?? LangEnum.GLOBAL_EDIT)}
        </Button>
      )}
      <Container maxWidth="xl">
        <Box
          sx={{
            paddingX: mobileWidth ? 2 : 3,
          }}
        >
          {children}
        </Box>
      </Container>
    </Box>
  );
};

export default Section;
